import { spawnQuad, spawnSprite } from '../vfx-pool';
import { texPolyBand, texPolyFill, texShard } from '../vfx-tex2';
import { FLAT, rnd, yawOf } from './util';
/* ============================================================
   風格 A 的五種基本筆畫
   ============================================================

   整個 A 方向只用這五筆組成，從主砲到大招都一樣 ——
   **一致性就是「有設計」的定義**。第三版的問題之一是每一招各用各的
   零件（火球、煙柱、揚塵、緞帶、光環），所以十招看起來像十個人做的。

     core   實心多邊形    「打出去／打進去的那一點」
     band   多邊形厚帶    「衝擊擴出去」（面向鏡頭）
     gband  躺平的厚帶    「地面被震開」（貼在地上，不會穿地）
     shard  三角碎片      「被打下來的一塊」← 記憶點
     scorch 焦痕          「這裡剛剛被打過」（留一秒）

   共同規則：**一般混色、不透明度 1、幾乎不漸淡**（`pow ≤ 0.7`）。
   漸淡是煙的行為；硬邊的東西只會消失，不會愈來愈透明。 */

/** 白光三階。無彩，辨識度靠明度差與形狀（使用者 2026-08-05 拍板）。 */
export var GC = { hot: 0xffffff, acc: 0xdce8f2, ink: 0x151c26, chip: 0x93a0b2 };

/** 實心多邊形。`n` 給邊數：6 = 機械（槍口、爆心），5 = 命中點。 */
export function core(n: number, x: number, y: number, z: number,
  s0: number, s1: number, t: number, c: number, op?: number) {
  spawnSprite(texPolyFill(n), x, y, z, {
    normal: true, c: c, s: s0, s1: s1, t: t,
    op: op === undefined ? 1 : op, pow: 0.6, ease: 1.5, rot: rnd(0, 6.283)
  });
}

/** 多邊形厚帶（面向鏡頭）。厚是重點 —— 細線是「標記」的語言。 */
export function band(x: number, y: number, z: number, th: number,
  s0: number, s1: number, t: number, c: number, op?: number) {
  spawnSprite(texPolyBand(6, th), x, y, z, {
    normal: true, c: c, s: s0, s1: s1, t: t,
    op: op === undefined ? 1 : op, pow: 0.6, ease: 2.6, rot: rnd(0, 6.283)
  });
}

/** 躺平在地上的厚帶。**地面回饋一定要用這個而不是 `band`** ——
    面向鏡頭的環畫在地面高度會穿進地裡，讀成半個環。 */
export function gband(x: number, z: number, th: number,
  s0: number, s1: number, t: number, c: number, op?: number) {
  spawnQuad(texPolyBand(6, th), x, 0.12, z, FLAT, rnd(0, 6.283), 0, {
    normal: true, c: c, s: s0, s1: s1, t: t,
    op: op === undefined ? 0.9 : op, pow: 0.7, ease: 2.4
  });
}

/** 一片三角碎片。**A 方向的記憶點。**

    走一般混色、吃重力、會自轉、幾乎不漸淡 —— 四件事缺一就讀不成碎片。
    尺寸的判準是**像素不是公尺**：遊戲鏡頭在 33 公尺高，
    小於 0.8 公尺的東西在畫面上只有十幾個像素，等於不存在。 */
export function shard(x: number, y: number, z: number, ang: number, spread: number,
  color: number, speed: number, size: number) {
  var a = ang + rnd(-spread, spread);
  var v = speed * rnd(0.5, 1.3);
  spawnQuad(texShard(), x, y, z, FLAT, yawOf(a), 0, {
    normal: true, c: color, s: size * rnd(0.7, 1.3), s1: size * rnd(0.5, 0.9),
    t: rnd(0.26, 0.46), op: 1, pow: 0.5, ease: 1,
    vx: Math.sin(a) * v, vy: rnd(3, 8), vz: Math.cos(a) * v, drag: 2.2, grav: 30,
    spin: rnd(-9, 9)
  });
}

/** 碎片扇：n 片一起往同一側噴。一片白、兩片灰 —— 白的是「我的傷害」，
    灰的是「被打下來的裝甲」，白光方案的辨識度全靠這個明度階。 */
export function fan(x: number, y: number, z: number, ang: number, spread: number,
  n: number, speed: number, size: number) {
  for (var i = 0; i < n; i++) {
    shard(x, y, z, ang, spread, i % 3 === 0 ? GC.hot : GC.chip, speed, size);
  }
}

/** 焦痕：地上一片**暗色**的硬邊多邊形，留一秒多。

    這是取代第三版「揚塵」的東西。揚塵是柔邊的、亮的、會飄，
    在硬邊語言裡讀成一團髒霧；焦痕是暗的、有明確輪廓、釘在原地 ——
    它說的是「這裡剛剛被打過」，而那一秒的殘留就是重量感的來源。 */
export function scorch(x: number, z: number, r: number, life: number) {
  spawnQuad(texPolyFill(6), x, 0.08, z, FLAT, rnd(0, 6.283), 0, {
    normal: true, c: GC.ink, s: r * 0.5, s1: r, t: life,
    op: 0.55, pow: 1.6, ease: 3.0
  });
}
